import { Link, useParams } from "react-router-dom";
import { ExternalLink, Shield } from "lucide-react";
import { useMe, useOAuthDecision, useOAuthRequest } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SignInPage from "./SignIn";

export default function OAuthConsentPage() {
  const { id } = useParams<{ id: string }>();
  const me = useMe();
  const request = useOAuthRequest(id);
  const decision = useOAuthDecision();

  if (!id) {
    return <Centered>Invalid request id.</Centered>;
  }
  if (me.isLoading) return <Centered>Checking your session…</Centered>;
  if (!me.data) return <SignInPage returnTo={`/oauth/consent/${id}`} />;
  if (request.isLoading) return <Centered>Loading authorization request…</Centered>;

  if (request.isError || !request.data) {
    return (
      <Centered>
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-8">
            <Shield className="mx-auto size-10 text-muted-foreground/60" />
            <h2 className="mt-4 text-lg font-semibold">Authorization request unavailable</h2>
            <p className="mt-2 text-sm text-muted-foreground">This request has expired or has already been decided.</p>
            <Button asChild className="mt-6">
              <Link to="/settings/connect">Back to settings</Link>
            </Button>
          </CardContent>
        </Card>
      </Centered>
    );
  }

  const req = request.data;
  const decide = (allow: boolean) =>
    decision.mutate(
      { id, allow },
      {
        onSuccess: (d) => window.location.assign(d.redirectTo),
      },
    );

  return (
    <Centered>
      <Card className="w-full max-w-md">
        <CardContent className="py-6">
          <div className="text-center">
            <Shield className="mx-auto size-10 text-primary/70" />
            <h1 className="mt-4 text-xl font-semibold tracking-tight">Authorize {req.clientName}</h1>
            <p className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground">
              <ExternalLink className="size-3.5" />
              {req.redirectOrigin}
            </p>
          </div>

          <div className="mt-6 rounded-lg border bg-muted/30 p-4 text-sm leading-relaxed">
            Allow this app to read, search, create and patch notes in your spaces on your behalf?
            {req.scopes && req.scopes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {req.scopes.map((s) => (
                  <code key={s} className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                    {s}
                  </code>
                ))}
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            Signed in as <span className="font-medium text-foreground">{me.data.displayName || me.data.username}</span>. Notes written by this app are attributed to “{req.clientName}”.
          </p>

          {decision.isError && <p className="mt-3 text-sm text-destructive">{errorMessage(decision.error)}</p>}

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => decide(false)} disabled={decision.isPending}>
              Deny
            </Button>
            <Button onClick={() => decide(true)} disabled={decision.isPending}>
              {decision.isPending ? "Processing…" : "Allow"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-app items-center justify-center bg-background px-4 text-sm text-muted-foreground">{children}</div>;
}
